// src/pages/Auditorias/components/AuditoriasTable.js
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ESTADO_AUDITORIA_LABEL, formatarRelativo,
  descreverProgresso, percentagemProgresso
} from '../utils/formatacao';

/**
 * Tabela de gestão geral.
 *
 * As gravidades aparecem separadas (graves / atenção) em vez do total_problemas
 * do mestre: um número único não distinguiria o que exige acção do que só
 * merece confirmação.
 */
const AuditoriasTable = ({ auditorias, onVerParecer, onAtualizar, onExcluir }) => {
  if (!auditorias.length) {
    return (
      <div className="aud-vazio-caixa">
        <p>Nenhuma auditoria ainda.</p>
        <p className="aud-vazio-dica">Comece por auditar um processo com o botão acima.</p>
      </div>
    );
  }

  return (
    <table className="aud-tabela">
      <thead>
        <tr>
          <th>Cliente / processo</th>
          <th>Advogado</th>
          <th>Problemas</th>
          <th>Peças</th>
          <th>Estado</th>
          <th aria-label="Acções" />
        </tr>
      </thead>
      <tbody>
        {auditorias.map((a) => {
          const emCurso = a.estado === 'em_execucao' || a.estado === 'pendente';
          const progresso = emCurso ? descreverProgresso(a) : null;
          const percentagem = emCurso ? percentagemProgresso(a) : null;

          return (
            <tr key={a.id}>
              <td>
                <Link to={`/auditorias/${a.id}`} className="aud-link-forte">
                  {a.nome_cliente || `Cliente ${a.cod_cli}`}
                </Link>
                <div className="aud-mono aud-sub">{a.cod_processo}</div>
                {a.num_processo && <div className="aud-sub">{a.num_processo}</div>}
              </td>

              <td className="aud-sub">{a.user_email}</td>

              <td>
                {/* No modo parecer único não há gravidade por peça. Mostrar
                    "nenhum" seria dizer que o processo está limpo, quando o que
                    se passa é que não foi conferido peça a peça. */}
                {a.avaliar_peca_individual === false ? (
                  <>
                    <span className="aud-badge aud-modo-unico">parecer único</span>
                    <div className="aud-sub">sem análise por peça</div>
                  </>
                ) : (
                  <>
                    {a.graves > 0 && (
                      <span className="aud-badge aud-grav-grave">{a.graves} graves</span>
                    )}
                    {a.atencao > 0 && (
                      <div className="aud-sub">{a.atencao} atenção</div>
                    )}
                    {!a.graves && !a.atencao && (
                      <span className="aud-sub">{a.total_avaliados ? 'nenhum' : '—'}</span>
                    )}
                  </>
                )}
                {a.ilegiveis > 0 && (
                  <div className="aud-sub aud-sub-alerta">{a.ilegiveis} ilegíveis</div>
                )}
              </td>

              <td className="aud-td-progresso">
                {emCurso ? (
                  <>
                    {progresso.passo && (
                      <div className="aud-passo">{progresso.passo}</div>
                    )}
                    <div className="aud-progresso-vivo">
                      {progresso.etapa}
                      {progresso.contador && <> · {progresso.contador}</>}
                    </div>
                    {percentagem !== null && (
                      <div className="aud-barra">
                        <div className="aud-barra-cheia" style={{ width: `${percentagem}%` }} />
                      </div>
                    )}
                    {progresso.pormenor && (
                      <div className="aud-sub aud-truncar" title={progresso.pormenor}>
                        {progresso.pormenor}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <span>{a.avaliados} / {a.avaliaveis}</span>
                    <div className="aud-sub">{formatarRelativo(a.ultima_atualizacao)}</div>
                  </>
                )}
              </td>

              <td>
                <span className={`aud-badge aud-estado-${a.estado}`}>
                  {ESTADO_AUDITORIA_LABEL[a.estado] || a.estado}
                </span>
                {a.validacao === 'validada' && (
                  <div className="aud-sub">validada por {a.validado_por}</div>
                )}
                {a.validacao === 'pendente' && a.estado === 'concluida' && (
                  <div className="aud-sub aud-sub-alerta">por validar</div>
                )}
              </td>

              <td className="aud-accoes">
                <button
                  type="button"
                  className="aud-icone"
                  title={a.parecer_id ? 'Ver parecer final' : 'Parecer por emitir'}
                  aria-label="Ver parecer final"
                  disabled={!a.parecer_id}
                  onClick={() => onVerParecer(a.id)}
                >
                  ▤
                </button>
                <Link to={`/auditorias/${a.id}`} className="aud-link">abrir</Link>
                <button
                  type="button"
                  className="aud-link"
                  disabled={emCurso}
                  onClick={() => onAtualizar(a)}
                  title={emCurso ? 'Já está a correr' : 'Procurar movimentos novos'}
                >
                  atualizar
                </button>
                {/* Só administradores — apagar é irreversível. Desactivado
                    enquanto corre: o worker está a escrever nestas linhas e o
                    servidor recusaria com 409 de qualquer maneira. */}
                {onExcluir && (
                  <button
                    type="button"
                    className="aud-link aud-link-perigo"
                    disabled={emCurso}
                    onClick={() => onExcluir(a)}
                    title={emCurso ? 'Cancele a execução antes de apagar' : 'Apagar esta auditoria e tudo o que lhe pertence'}
                  >
                    excluir
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default AuditoriasTable;
